import { Box, Typography } from '@mui/material'
// ProgressUpload.js
import React, { useEffect, useState } from 'react'

import LinearProgress from '@mui/material/LinearProgress'
import PropTypes from 'prop-types'
import { autoIngest } from 'api/upload'
import { styled } from '@mui/system'
import { useNotification } from 'hooks/useNotification'
import { useUppy } from 'contexts/UppyContext'
import { useTheme } from '@mui/material/styles'

const ProgressUploadContainer = styled('div')({
  position: 'absolute',
  left: 260,
  bottom: 10,
  zIndex: 9999,
  width: 300,
  height: 50,
  // backgroundColor: '#fff',
  boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
  borderRadius: 8,
  border: '1px solid #2C2C2C',
  display: 'none',
  transform: 'translateY(10px)', // Slightly translate down for effect
  transition: 'opacity 0.3s ease, transform 0.3s ease', // Transition effects
  '&.visible': {
    display: 'block',
    transform: 'translateY(0)'
  }
})

const ProgressUpload = React.memo(() => {
  const uppy = useUppy()
  const [isVisible, setIsVisible] = useState(false)
  const [percentage, setPercentage] = useState(0)
  const notify = useNotification()
  const theme = useTheme()

  const handleUploadStart = React.useCallback(() => {
    setIsVisible(true)
  }, [])

  const handleUploadComplete = React.useCallback(
    async (result) => {
      const successData = result?.successful
        ?.filter((file) => file.meta.option === 'magic-upload')
        .map((file) => {
          return {
            name: file.name,
            type: file.type,
            extension: file.extension,
            relative_path: file.meta.relativePath || file.name,
            key: file.s3Multipart.key
          }
        })

      uppy.clear()
      setIsVisible(false)

      if (successData.length > 0) {
        await autoIngest(successData)
      }
      notify({ type: 'success', message: 'Upload completed, please check processing in Task interface' })
    },
    [uppy, notify]
  )

  const uploadProgress = React.useCallback((progress) => {
    setPercentage(progress)
  }, [])

  useEffect(() => {
    uppy.on('upload-start', handleUploadStart)
    uppy.on('complete', handleUploadComplete)
    uppy.on('progress', uploadProgress)

    return () => {
      uppy.off('upload-start', handleUploadStart)
      uppy.off('complete', handleUploadComplete)
      uppy.off('progress', uploadProgress)
    }
  }, [uppy, handleUploadStart, handleUploadComplete, uploadProgress])

  return (
    <ProgressUploadContainer className={isVisible ? 'visible' : ''} sx={{ backgroundColor: theme.palette.background.paper }}>
      <Typography variant="h5" align="center" sx={{ paddingTop: 1 }}>
        Uploading...
      </Typography>
      <Box sx={{ width: '100%', px: 1 }}>
        <LinearProgressWithLabel value={percentage} />
      </Box>
    </ProgressUploadContainer>
  )
})

ProgressUpload.displayName = 'ProgressUpload'

export default ProgressUpload

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  )
}

LinearProgressWithLabel.propTypes = {
  value: PropTypes.number.isRequired
}
